import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Slider } from "@/components/ui/slider";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { BookOpen, Plus, LogOut, BookMarked, Clock, CheckCircle, Trash2, MoreVertical, FileText, Upload, Sparkles, CreditCard, Gift, Percent, Pencil } from "lucide-react";
import Sidebar from "@/components/Sidebar";
import TropesSelector from "@/components/TropesSelector";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ user, onLogout }) => {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePromo, setActivePromo] = useState(null);
  const [showProjectName, setShowProjectName] = useState(false);
  const [showGenreChoice, setShowGenreChoice] = useState(false);
  const [showContentChoice, setShowContentChoice] = useState(false);
  const [showNewBook, setShowNewBook] = useState(false);
  const [showImportConfig, setShowImportConfig] = useState(false);
  const [showCharacterDev, setShowCharacterDev] = useState(false);
  const [showBookIdeas, setShowBookIdeas] = useState(false);
  const [bookIdeas, setBookIdeas] = useState([]);
  const [selectedIdea, setSelectedIdea] = useState(null);
  const [refinePrompt, setRefinePrompt] = useState("");
  const [generatingIdeas, setGeneratingIdeas] = useState(false);
  const [showWritingStyle, setShowWritingStyle] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewText, setPreviewText] = useState("");
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [showConfirmCreate, setShowConfirmCreate] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [generationProgress, setGenerationProgress] = useState({
    percentage: 0,
    currentChapter: 0,
    totalChapters: 0,
    status: "Preparing..."
  });
  const [userCredits, setUserCredits] = useState(0);
  const [creating, setCreating] = useState(false);
  const [bookToDelete, setBookToDelete] = useState(null);
  const [bookToEdit, setBookToEdit] = useState(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [savingTitle, setSavingTitle] = useState(false);
  const [projectTitle, setProjectTitle] = useState("");
  const [contentMode, setContentMode] = useState("fresh"); // "fresh" or "import"
  const [uploadedFile, setUploadedFile] = useState(null);
  const [analyzedContent, setAnalyzedContent] = useState(null);
  const [importConfig, setImportConfig] = useState({
    tone: "engaging",
    chapters: 15,
    tropes: "",
    targetWordCount: 50000,
  });
  const [selectedThemes, setSelectedThemes] = useState([]);
  const [selectedTropes, setSelectedTropes] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [writingStyle, setWritingStyle] = useState({
    tone: "engaging",
    perspective: "third-person",
    authorStyle: "descriptive",
    pacing: "moderate",
    authorInspiration: "none",
    bookStructure: "three-act",
    specialInstructions: "",
  });
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    premise: "",
    genre: "fiction",
    structure_type: "auto",
    total_chapters: "15",
    targetWordCount: 50000,
    includeCaseStudies: false,
  });
  const navigate = useNavigate();
  
  const [showResumeDialog, setShowResumeDialog] = useState(false);
  const [restoringProgress, setRestoringProgress] = useState(false);
  const [currentDraftId, setCurrentDraftId] = useState(null); // Track which draft is being edited
  
  // Auto-save form data to localStorage as user types
  useEffect(() => {
    // Only auto-save if there's actual data to save
    if (formData.premise || formData.title || formData.description || selectedThemes.length > 0) {
      const autoSaveData = {
        formData,
        projectTitle,
        selectedThemes,
        selectedTropes,
        characters,
        writingStyle,
        selectedIdea,
        bookIdeas,
        timestamp: new Date().toISOString()
      };
      
      // Debounce: save after 2 seconds of no changes
      const timeoutId = setTimeout(() => {
        localStorage.setItem('bookCreationAutoSave', JSON.stringify(autoSaveData));
        console.log('[Auto-Save] Form data saved to localStorage');
      }, 2000);
      
      return () => clearTimeout(timeoutId);
    }
  }, [formData, projectTitle, selectedThemes, selectedTropes, characters, writingStyle, selectedIdea, bookIdeas]);

  // Clear progress after successful book generation
  const clearSavedProgress = () => {
    localStorage.removeItem('bookCreationProgress');
    console.log('[Progress] Cleared saved progress');
  };

  const resumeDraft = async (book) => {
    console.log('[Draft] Resuming draft book:', book.id);
    
    try {
      // Store the draft ID so we can update it instead of creating a new one
      setCurrentDraftId(book.id);
      
      // Restore form data from draft
      if (book.draft_data) {
        const draftData = book.draft_data;
        
        // Restore basic form data
        setFormData({
          title: book.title || '',
          description: book.description || '',
          premise: draftData.premise || '',
          genre: book.genre || 'fiction',
          structure_type: book.structure_type || 'manual',
          total_chapters: book.total_chapters?.toString() || '15',
          targetWordCount: draftData.target_word_count || 50000
        });
        
        // Restore project title
        setProjectTitle(book.title || '');
        
        // Restore themes and tropes
        setSelectedThemes(draftData.themes_array || []);
        setSelectedTropes(draftData.tropes_array || []);
        
        // Restore characters
        setCharacters(draftData.characters || []);
        
        // Restore writing style with defaults to prevent undefined errors
        const defaultWritingStyle = {
          tone: "engaging",
          perspective: "third-person",
          authorStyle: "descriptive",
          pacing: "moderate",
          authorInspiration: "none",
          bookStructure: "three-act",
          specialInstructions: "",
        };
        if (draftData.writing_style_full) {
          setWritingStyle({
            ...defaultWritingStyle,
            ...draftData.writing_style_full
          });
        }
        
        // Restore imported chapter outline if exists
        if (book.imported_chapter_outline) {
          setAnalyzedContent({
            chapters: book.imported_chapter_outline,
            chapter_count: book.imported_chapter_outline.length
          });
        }
        
        console.log('[Draft] Draft data restored');
        toast.success('📝 Draft loaded! Continue where you left off.');
        
        // Open the main book creation form
        setShowNewBook(true);
      } else {
        // Fallback: just open form with basic data
        setFormData({
          title: book.title || '',
          description: book.description || '',
          genre: book.genre || 'fiction',
          total_chapters: book.total_chapters?.toString() || '15'
        });
        setProjectTitle(book.title || '');
        setShowNewBook(true);
        toast.info('Opening draft...');
      }
    } catch (error) {
      console.error('[Draft] Failed to resume draft:', error);
      toast.error('Failed to load draft. Please try again.');
    }
  };

  const saveDraftAndReturn = async (retryCount = 0) => {
    // Save current progress as draft book (create new or update existing)
    console.log('[Draft] Saving current progress as draft');
    
    try {
      setCreating(true);
      
      if (retryCount > 0) {
        toast.loading(`Retrying save... (attempt ${retryCount + 1}/3)`, { id: "draft-retry" });
      }
      
      const draftPayload = {
        title: formData.title || projectTitle || "Untitled Book",
        premise: formData.premise,
        description: formData.description,
        genre: formData.genre,
        structure_type: "manual",
        total_chapters: parseInt(formData.total_chapters),
        target_word_count: formData.targetWordCount,
        themes: selectedThemes,
        selected_tropes: selectedTropes,
        characters: characters,
        writing_style: writingStyle,
        selected_idea: selectedIdea?.text || "",
        imported_chapter_outline: analyzedContent?.chapters || null,
        include_case_studies: formData.includeCaseStudies,
        status: "draft"
      };
      
      let response;
      if (currentDraftId) {
        // Update existing draft
        console.log('[Draft] Updating existing draft:', currentDraftId);
        response = await axios.put(`${API}/books/${currentDraftId}/update-draft`, draftPayload);
        console.log('[Draft] Draft updated:', response.data.book_id);
      } else {
        // Create new draft
        console.log('[Draft] Creating new draft');
        response = await axios.post(`${API}/books/save-draft`, draftPayload);
        console.log('[Draft] Draft created:', response.data.book_id);
      }
      
      if (retryCount > 0) {
        toast.dismiss("draft-retry");
      }
      
      // Close all dialogs first
      setShowNewBook(false);
      setShowGenreChoice(false);
      setShowContentChoice(false);
      setShowImportConfig(false);
      setShowCharacterDev(false);
      setShowBookIdeas(false);
      setShowWritingStyle(false);
      setShowPreview(false);
      setShowConfirmCreate(false);
      
      // Give a tiny delay to ensure backend write is complete
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Force reload by setting loading state
      setLoading(true);
      
      // Reload books to show updated draft
      try {
        const response = await axios.get(`${API}/books`);
        console.log(`[Draft] Reloaded ${response.data.length} books`);
        setBooks(response.data);
        setLoading(false);
        toast.success("📝 Draft saved! Your book appears on the dashboard.");
      } catch (loadError) {
        console.error('[Draft] Failed to reload books:', loadError);
        setLoading(false);
        toast.success("📝 Draft saved! Refresh the page to see your book.");
      }
      
      // Clear draft ID and auto-save data after returning to dashboard
      setCurrentDraftId(null);
      localStorage.removeItem('bookCreationAutoSave');
      setCreating(false);
    } catch (error) {
      console.error('[Draft] Failed to save draft:', error);
      
      if (retryCount > 0) {
        toast.dismiss("draft-retry");
      }
      
      // Auto-retry up to 2 times
      if (retryCount < 2) {
        console.log(`[Draft] Retrying save (attempt ${retryCount + 2}/3)...`);
        setCreating(false);
        setTimeout(() => saveDraftAndReturn(retryCount + 1), 2000);
        return;
      }
      
      toast.error("Failed to save draft after 3 attempts. Please try again.");
      setCreating(false);
    }
  };

  // Save draft and navigate to credits page (with retry logic)
  const saveDraftAndNavigateToCredits = async (retryCount = 0) => {
    console.log('[Draft] Saving book as draft before navigating to credits');
    
    try {
      setCreating(true);
      
      if (retryCount > 0) {
        toast.loading(`Retrying save... (attempt ${retryCount + 1}/3)`, { id: "draft-credit-retry" });
      }
      
      const draftPayload = {
        title: formData.title || "Untitled Book",
        premise: formData.premise,
        description: formData.description,
        genre: formData.genre,
        structure_type: "manual",
        total_chapters: parseInt(formData.total_chapters),
        target_word_count: formData.targetWordCount,
        themes: selectedThemes,
        selected_tropes: selectedTropes,
        characters: characters,
        writing_style: writingStyle,
        selected_idea: selectedIdea?.text || "",
        imported_chapter_outline: analyzedContent?.chapters || null,
        status: "draft"
      };
      
      const response = await axios.post(`${API}/books/save-draft`, draftPayload);
      
      if (retryCount > 0) {
        toast.dismiss("draft-credit-retry");
      }
      
      console.log('[Draft] Draft book saved:', response.data.book_id);
      toast.success("Draft saved! Continue when you're ready.");
      
      clearSavedProgress();
      setShowConfirmCreate(false);
      setCreating(false);
      
      await loadBooks();
      navigate('/credits');
    } catch (error) {
      console.error('[Draft] Failed to save draft:', error);
      
      if (retryCount > 0) {
        toast.dismiss("draft-credit-retry");
      }
      
      // Auto-retry up to 2 times
      if (retryCount < 2) {
        console.log(`[Draft] Retrying save (attempt ${retryCount + 2}/3)...`);
        setCreating(false);
        setTimeout(() => saveDraftAndNavigateToCredits(retryCount + 1), 2000);
        return;
      }
      
      toast.error("Failed to save draft after 3 attempts. Please try again.");
      setCreating(false);
    }
  };

  useEffect(() => {
    loadBooks();
    loadUserCredits();
    loadActivePromo();
    
    // Check for auto-saved data
    const autoSavedData = localStorage.getItem('bookCreationAutoSave');
    if (autoSavedData) {
      try {
        const parsed = JSON.parse(autoSavedData);
        const savedTime = new Date(parsed.timestamp);
        const hoursSinceSave = (new Date() - savedTime) / (1000 * 60 * 60);
        
        // Only restore if saved within last 24 hours
        if (hoursSinceSave < 24) {
          console.log('[Auto-Save] Found auto-saved data from', savedTime);
          toast.info("💾 We found unsaved work. Click 'Create New Book' to restore it.", {
            duration: 8000
          });
        } else {
          // Clear old auto-save data
          localStorage.removeItem('bookCreationAutoSave');
        }
      } catch (error) {
        console.error('[Auto-Save] Failed to parse auto-saved data:', error);
        localStorage.removeItem('bookCreationAutoSave');
      }
    }
    
    // Clear any old localStorage progress - we now use draft books instead
    localStorage.removeItem('bookCreationProgress');
    sessionStorage.removeItem('returnFromCredits');
    
    console.log('[Dashboard] Using draft books instead of resume dialog');
  }, []);

  // Auto-save progress when user makes changes (debounced)
  useEffect(() => {
    // Only save if we have meaningful data
    if (formData.title || formData.premise || selectedThemes.length > 0 || characters.length > 0) {
      const timer = setTimeout(() => {
        saveProgressToLocalStorage();
      }, 1000); // Save 1 second after user stops typing/selecting
      
      return () => clearTimeout(timer);
    }
  }, [formData, contentMode, selectedThemes, selectedTropes, characters, bookIdeas, selectedIdea, writingStyle]);

  // Open form after progress restoration is complete
  useEffect(() => {
    if (restoringProgress) {
      // Wait for state to fully propagate
      const timer = setTimeout(() => {
        console.log('[Progress] State restored, opening form with data:', {
          projectTitle,
          bookTitle: formData.title,
          premise: formData.premise,
          themesCount: selectedThemes.length
        });
        setShowNewBook(true);
        setRestoringProgress(false);
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [restoringProgress, formData.title, formData.premise, projectTitle, selectedThemes.length]);

  const loadBooks = async (retryCount = 0) => {
    try {
      const response = await axios.get(`${API}/books`);
      console.log(`[Dashboard] Loaded ${response.data.length} books from API`);
      
      // Debug: Log book statuses
      const statusCounts = {};
      response.data.forEach(book => {
        const status = book.status || 'undefined';
        statusCounts[status] = (statusCounts[status] || 0) + 1;
      });
      console.log('[Dashboard] Books by status:', statusCounts);
      
      setBooks(response.data);
    } catch (error) {
      console.error('[Dashboard] Failed to load books:', error);
      // Auto-retry up to 2 times with increasing delay
      if (retryCount < 2) {
        console.log(`[Dashboard] Retrying loadBooks (attempt ${retryCount + 2}/3)...`);
        setTimeout(() => loadBooks(retryCount + 1), 1000 * (retryCount + 1));
        return; // Don't show error toast yet
      }
      toast.error("Failed to load books. Please refresh the page.");
    } finally {
      if (retryCount === 0 || retryCount >= 2) {
        setLoading(false);
      }
    }
  };

  const loadUserCredits = async () => {
    try {
      const response = await axios.get(`${API}/credits`);
      setUserCredits(response.data.credits || 0);
    } catch (error) {
      console.error("Failed to load credits");
    }
  };

  const loadActivePromo = async () => {
    try {
      const response = await axios.get(`${API}/promos/active`);
      if (response.data.has_promo) {
        setActivePromo(response.data);
      }
    } catch (error) {
      console.error("Failed to load promo:", error);
    }
  };

  const handleDeleteBook = async () => {
    if (!bookToDelete) return;
    
    try {
      await axios.delete(`${API}/books/${bookToDelete.id}`);
      toast.success("Book deleted successfully");
      setBookToDelete(null);
      loadBooks();
    } catch (error) {
      toast.error("Failed to delete book");
    }
  };

  const handleEditTitle = async () => {
    if (!bookToEdit || !editingTitle.trim()) return;
    
    setSavingTitle(true);
    try {
      await axios.put(`${API}/books/${bookToEdit.id}/title`, {
        title: editingTitle.trim()
      });
      toast.success("Title updated successfully");
      setBookToEdit(null);
      setEditingTitle("");
      loadBooks();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update title");
    } finally {
      setSavingTitle(false);
    }
  };

  const addTrope = (trope) => {
    const currentTropes = importConfig.tropes.trim();
    const newTropes = currentTropes 
      ? `${currentTropes}, ${trope}` 
      : trope;
    setImportConfig({...importConfig, tropes: newTropes});
    toast.success(`Added: ${trope}`);
  };

  const handleGenerateIdeas = async () => {
    setGeneratingIdeas(true);
    setShowCharacterDev(false);
    setShowBookIdeas(true);

    try {
      // Prepare the request payload
      const charactersList = characters.length > 0 
        ? characters.map(c => `${c.name} (${c.role}): ${c.description}`).join('; ')
        : '';
      
      const themesText = selectedThemes.length > 0 ? selectedThemes.join(', ') : '';
      const tropesText = selectedTropes.length > 0 ? selectedTropes.join(', ') : '';
      const themesAndTropes = [themesText, tropesText].filter(Boolean).join('. ');
      
      const payload = {
        title: uploadedFile?.name.replace(/\.[^/.]+$/, "") || projectTitle || "Untitled",
        premise: formData.premise,
        description: `${formData.description || 'Imported content'} ${themesAndTropes ? `Themes & Tropes: ${themesAndTropes}.` : ''} ${charactersList ? `Characters: ${charactersList}` : ''}`,
        genre: formData.genre,
        target_audience: "adult",
        setting_period: "contemporary",
        themes: themesAndTropes || "",
        creation_mode: contentMode === "import" ? "import" : "original",
        imported_content: analyzedContent ? {
          characters: analyzedContent.characters,
          plot_points: [],
          themes: [],
          setting: analyzedContent.summary
        } : null,
      };

      const response = await axios.post(`${API}/books/generate-ideas`, payload);
      setBookIdeas(response.data.ideas || []);
      toast.success("Ideas generated!");
    } catch (error) {
      toast.error("Failed to generate ideas. Please try again.");
      setShowBookIdeas(false);
      setShowCharacterDev(true);
    } finally {
      setGeneratingIdeas(false);
    }
  };

  const handleRefineIdeas = async () => {
    if (!refinePrompt.trim()) {
      toast.error("Please enter refinement guidance");
      return;
    }

    setGeneratingIdeas(true);

    try {
      const charactersList = characters.length > 0 
        ? characters.map(c => `${c.name} (${c.role}): ${c.description}`).join('; ')
        : '';
      
      const themesText = selectedThemes.length > 0 ? selectedThemes.join(', ') : '';
      const tropesText = selectedTropes.length > 0 ? selectedTropes.join(', ') : '';
      const themesAndTropes = [themesText, tropesText].filter(Boolean).join('. ');
      
      const payload = {
        title: uploadedFile?.name.replace(/\.[^/.]+$/, "") || projectTitle || "Untitled",
        premise: formData.premise,
        description: `${formData.description || 'Imported content'} ${themesAndTropes ? `Themes & Tropes: ${themesAndTropes}.` : ''} ${charactersList ? `Characters: ${charactersList}` : ''}`,
        genre: formData.genre,
        target_audience: "adult",
        setting_period: "contemporary",
        themes: themesAndTropes || "",
        refine_prompt: refinePrompt,
        previous_ideas: bookIdeas,
      };

      const response = await axios.post(`${API}/books/refine-ideas`, payload);
      setBookIdeas(response.data.ideas || []);
      setRefinePrompt("");
      toast.success("Ideas refined!");
    } catch (error) {
      toast.error("Failed to refine ideas. Please try again.");
    } finally {
      setGeneratingIdeas(false);
    }
  };

  const handleGeneratePreview = async (retryCount = 0) => {
    setGeneratingPreview(true);
    setShowWritingStyle(false);
    setShowPreview(true);

    // Show loading toast for long operations
    if (retryCount === 0) {
      toast.loading("Generating preview... (this may take up to 60 seconds)", { id: "preview-loading" });
    }

    try {
      // Prepare the prompt with all user selections
      const charactersList = characters.length > 0 
        ? characters.map(c => `${c.name} (${c.role}): ${c.description}`).join('; ')
        : 'No characters defined yet';
      
      const themesText = selectedThemes.length > 0 ? `Themes: ${selectedThemes.join(', ')}` : '';
      const tropesText = selectedTropes.length > 0 ? `Tropes: ${selectedTropes.join(', ')}` : '';
      const themesAndTropesDisplay = [themesText, tropesText].filter(Boolean).join('\n') || 'None specified';
      
      // Get the selected idea text if available
      const selectedIdeaText = selectedIdea !== null && bookIdeas[selectedIdea] 
        ? bookIdeas[selectedIdea] 
        : '';
      
      const prompt = `Generate a compelling first page (approximately 300-400 words) for a ${formData.genre} book with the following specifications:

Title: ${formData.title || projectTitle || 'Untitled'}
Book Premise: ${formData.premise || 'Not specified'}
${selectedIdeaText ? `Book Idea/Concept: ${selectedIdeaText}` : ''}

Genre: ${formData.genre}
Tone: ${importConfig.tone}
${themesAndTropesDisplay}
${formData.genre.toLowerCase() === 'fiction' ? `Characters: ${charactersList}` : ''}
Narrative Perspective: ${writingStyle.perspective}
Writing Style: ${writingStyle.authorStyle}
Pacing: ${writingStyle.pacing}
${writingStyle.authorInspiration !== 'none' ? `Author Inspiration: Write in the style of ${writingStyle.authorInspiration}` : ''}
Book Structure: ${writingStyle.bookStructure}

IMPORTANT: The preview MUST be about the book premise and concept described above. ${formData.genre.toLowerCase() === 'non-fiction' ? 'For non-fiction, write an engaging introduction that introduces the topic, explains why it matters, and sets expectations for what readers will learn.' : 'Create an engaging opening that hooks the reader immediately. Show, don\'t tell. Set the scene vividly.'}`;

      const response = await axios.post(`${API}/generate-preview`, {
        prompt: prompt,
        genre: formData.genre,
        tone: importConfig.tone,
      }, {
        timeout: 90000 // 90 second timeout on frontend (backend has 60s)
      });

      toast.dismiss("preview-loading");
      toast.dismiss("preview-retry");
      setPreviewText(response.data.preview);
      toast.success("Preview generated!");
      setGeneratingPreview(false);
    } catch (error) {
      console.error("Preview generation failed:", error);
      
      // Check if it was a timeout
      const isTimeout = error.code === 'ECONNABORTED' || 
                        error.response?.status === 504 ||
                        error.message?.includes('timeout');
      
      // Auto-retry up to 2 times
      if (retryCount < 2) {
        console.log(`[Preview] Retrying (attempt ${retryCount + 2}/3)...`);
        toast.dismiss("preview-loading");
        toast.loading(`${isTimeout ? 'Timed out - ' : ''}Retrying preview... (attempt ${retryCount + 2}/3)`, { id: "preview-retry" });
        setTimeout(() => handleGeneratePreview(retryCount + 1), 2000);
        return;
      }
      
      toast.dismiss("preview-loading");
      toast.dismiss("preview-retry");
      
      const errorMsg = isTimeout 
        ? "Preview generation timed out. The AI service may be busy - please try again."
        : "Failed to generate preview after 3 attempts. Please try again.";
      
      toast.error(errorMsg);
      setShowPreview(false);
      setShowWritingStyle(true);
      setGeneratingPreview(false);
    }
  };

  const pollBookProgress = async (bookId, totalChapters) => {
    const maxAttempts = 300; // 5 minutes with 1 second intervals
    let attempts = 0;
    let consecutiveErrors = 0;
    const maxConsecutiveErrors = 5;

    const poll = async () => {
      try {
        const response = await axios.get(`${API}/books/${bookId}/generation-progress`);
        const progress = response.data;
        consecutiveErrors = 0; // Reset error counter on success
        
        setGenerationProgress({
          percentage: progress.percentage || 0,
          currentChapter: progress.current_chapter || 0,
          totalChapters: totalChapters,
          status: progress.status || "Generating..."
        });

        // Check if generation is complete
        if (progress.percentage >= 100 || progress.status === "completed") {
          setShowProgress(false);
          toast.success("Book generation complete!");
          await loadBooks();
          navigate(`/book/${bookId}`);
          return;
        }

        // Continue polling if not complete
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 1000); // Poll every second
        } else {
          // Timeout - but book was created, so reload to show it
          toast.info("Generation is taking longer than expected. Your book is saved - check 'My Books' for progress.");
          setShowProgress(false);
          await loadBooks(); // CRITICAL: Reload books so user sees the card
        }
      } catch (error) {
        console.error("Error polling progress:", error);
        consecutiveErrors++;
        attempts++;
        
        if (consecutiveErrors >= maxConsecutiveErrors) {
          // Too many consecutive errors - stop polling but show the book card
          toast.warning("Lost connection to generation progress. Your book is saved - check 'My Books' to monitor.");
          setShowProgress(false);
          await loadBooks(); // CRITICAL: Reload books so user sees the card
          return;
        }
        
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000); // Retry with longer interval on error
        } else {
          // Max attempts reached
          toast.info("Generation monitoring timed out. Your book is saved - check 'My Books' for progress.");
          setShowProgress(false);
          await loadBooks(); // CRITICAL: Reload books so user sees the card
        }
      }
    };

    poll();
  };

  const handleCreateBook = async (isRetry = false) => {
    // Check if user has enough credits
    if (userCredits < 1) {
      // Save current progress before redirecting to credits
      saveProgressToLocalStorage();
      sessionStorage.setItem('returnFromCredits', 'true');
      
      toast.info("💳 Saving your progress... Redirecting to purchase credits.");
      
      // Close dialogs
      setShowConfirmCreate(false);
      setShowNewBook(false);
      
      // Navigate to credits page after short delay
      setTimeout(() => {
        navigate("/credits");
      }, 1500);
      
      return;
    }

    // Close confirmation dialog
    setShowConfirmCreate(false);
    setCreating(true);
    
    if (isRetry) {
      toast.loading("Retrying book creation...", { id: "book-retry" });
    }

    try {
      // Prepare comprehensive book data
      const charactersList = characters.length > 0 
        ? characters.map(c => `${c.name} (${c.role}): ${c.description}`).join('; ')
        : '';
      
      const selectedIdeaText = selectedIdea !== null ? bookIdeas[selectedIdea] : '';
      
      const bookTitle = uploadedFile?.name.replace(/\.[^/.]+$/, "") || projectTitle || formData.title || "Untitled Book";
      
      const themesText = selectedThemes.length > 0 ? selectedThemes.join(', ') : '';
      const tropesText = selectedTropes.length > 0 ? selectedTropes.join(', ') : '';
      const themesAndTropes = [themesText, tropesText].filter(Boolean).join('. ');
      
      const bookDescription = `${selectedIdeaText ? `Idea: ${selectedIdeaText}. ` : ''}${formData.description || 'Imported content'}. ${themesAndTropes ? `Themes & Tropes: ${themesAndTropes}. ` : ''}${charactersList ? `Characters: ${charactersList}.` : ''}`;

      // Use formData.total_chapters as the primary source (user's selection), only fall back to importConfig for imported content
      const totalChapters = parseInt(formData.total_chapters) || importConfig.chapters || 15;
      const targetWordCount = formData.targetWordCount || importConfig.targetWordCount || 50000;
      const wordsPerChapter = Math.round(targetWordCount / totalChapters);

      const payload = {
        title: bookTitle,
        premise: formData.premise,
        description: bookDescription,
        genre: formData.genre,
        structure_type: "manual",
        total_chapters: totalChapters,
        target_word_count: targetWordCount,
        words_per_chapter: wordsPerChapter,
        tone: importConfig.tone,
        tropes: themesAndTropes,
        themes: selectedThemes,
        selected_tropes: selectedTropes,
        characters: characters,
        writing_style: writingStyle,
        selected_idea: selectedIdeaText,
        // Include imported chapter outline if available (for non-fiction)
        imported_chapter_outline: analyzedContent?.chapters || null,
        // Include case studies option for non-fiction
        include_case_studies: formData.includeCaseStudies
      };
      
      // Log if we're using imported outline
      if (analyzedContent?.chapters) {
        console.log(`[Book Creation] Using imported outline with ${analyzedContent.chapters.length} chapters`);
      }

      // Create the book and start generation
      const response = await axios.post(`${API}/books/generate`, payload);
      
      if (isRetry) {
        toast.dismiss("book-retry");
      }
      
      const bookId = response.data.id || response.data.book_id;
      
      // Close all dialogs and show progress
      setShowPreview(false);
      setShowNewBook(false);
      setShowImportConfig(false);
      setShowCharacterDev(false);
      setShowBookIdeas(false);
      setShowWritingStyle(false);
      setShowProgress(true);
      
      // Initialize progress
      setGenerationProgress({
        percentage: 0,
        currentChapter: 0,
        totalChapters: totalChapters,
        status: "Starting generation..."
      });
      
      // Reset form and clear auto-save
      resetForm();
      localStorage.removeItem('bookCreationAutoSave');
      
      // IMMEDIATELY reload books so the card appears right away
      // This ensures users see their book even if polling fails
      await loadBooks();
      toast.success("Book created! Generation starting...");
      
      // Reload credits
      await loadUserCredits();
      
      // Start polling for progress
      pollBookProgress(bookId, totalChapters);
      
    } catch (error) {
      console.error("Book generation failed:", error);
      
      if (isRetry) {
        toast.dismiss("book-retry");
      }
      
      const errorMsg = error.response?.data?.detail || "Failed to create book";
      
      // Check if it's a retryable error (not a validation/credit error) and not already a retry
      const isRetryable = !isRetry && 
                          !errorMsg.toLowerCase().includes("credit") && 
                          !errorMsg.toLowerCase().includes("validation") && 
                          error.response?.status !== 400;
      
      // Handle rate limiting (429) - show friendly message, don't retry
      if (error.response?.status === 429) {
        toast.error(errorMsg, { duration: 8000 });
        setShowProgress(false);
        setCreating(false);
        return;
      }
      
      // Handle duplicate generation (409) - show friendly message, don't retry
      if (error.response?.status === 409) {
        toast.error(errorMsg, { duration: 5000 });
        setShowProgress(false);
        setCreating(false);
        return;
      }
      
      if (isRetryable) {
        console.log("[Book Creation] Retrying once after failure...");
        setCreating(false);
        setTimeout(() => handleCreateBook(true), 2000);
        return;
      }
      
      toast.error(errorMsg);
      setShowProgress(false);
    } finally {
      setCreating(false);
    }
  };

  const saveProgressToLocalStorage = () => {
    const progressData = {
      projectTitle,
      contentMode,
      importConfig,
      selectedThemes,
      selectedTropes,
      characters,
      bookIdeas,
      selectedIdea,
      refinePrompt,
      writingStyle,
      formData,
      timestamp: Date.now()
    };
    localStorage.setItem('bookCreationProgress', JSON.stringify(progressData));
    console.log('[Progress] Saved progress to localStorage:', {
      title: formData.title,
      premise: formData.premise,
      themesCount: selectedThemes.length,
      charactersCount: characters.length
    });
  };

  const restoreProgressFromLocalStorage = () => {
    try {
      const savedProgress = localStorage.getItem('bookCreationProgress');
      if (savedProgress) {
        const progressData = JSON.parse(savedProgress);
        
        console.log('[Progress] Restoring progress:', progressData);
        console.log('[Progress] formData being restored:', progressData.formData);
        console.log('[Progress] projectTitle being restored:', progressData.projectTitle);
        
        // Restore all state in a single batch
        const restoredFormData = progressData.formData || {
          title: "",
          description: "",
          premise: "",
          genre: "fiction",
          structure_type: "auto",
          total_chapters: "15",
          targetWordCount: 50000,
        };
        
        const restoredWritingStyle = progressData.writingStyle || {
          tone: "engaging",
          perspective: "third-person",
          authorStyle: "descriptive",
          pacing: "moderate",
          authorInspiration: "none",
          bookStructure: "three-act",
          specialInstructions: "",
        };
        
        // Set all state
        setProjectTitle(progressData.projectTitle || "");
        setContentMode(progressData.contentMode || "fresh");
        setImportConfig(progressData.importConfig || {
          tone: "engaging",
          chapters: 15,
          tropes: "",
          targetWordCount: 50000,
        });
        setSelectedThemes(progressData.selectedThemes || []);
        setSelectedTropes(progressData.selectedTropes || []);
        setCharacters(progressData.characters || []);
        setBookIdeas(progressData.bookIdeas || []);
        setSelectedIdea(progressData.selectedIdea || null);
        setRefinePrompt(progressData.refinePrompt || "");
        setWritingStyle(restoredWritingStyle);
        setFormData(restoredFormData);
        
        console.log('[Progress] State restoration complete');
        console.log('[Progress] formData after set:', restoredFormData);
        
        toast.success("📝 Your book creation progress has been restored!");
        return true;
      }
    } catch (error) {
      console.error("Error restoring progress:", error);
      toast.error("Failed to restore progress");
    }
    return false;
  };

  const resetForm = () => {
    setProjectTitle("");
    setUploadedFile(null);
    setContentMode("fresh");
    setImportConfig({
      tone: "engaging",
      chapters: 15,
      tropes: "",
      targetWordCount: 50000,
    });
    setSelectedThemes([]);
    setSelectedTropes([]);
    setCharacters([]);
    setBookIdeas([]);
    setSelectedIdea(null);
    setRefinePrompt("");
    setWritingStyle({
      tone: "engaging",
      perspective: "third-person",
      authorStyle: "descriptive",
      pacing: "moderate",
      authorInspiration: "none",
      bookStructure: "three-act",
    });
    setFormData({
      title: "",
      description: "",
      genre: "fiction",
      structure_type: "auto",
      total_chapters: "15",
      targetWordCount: 50000,
    });
    clearSavedProgress();
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { label: "Draft", icon: BookMarked, color: "bg-stone-200 text-stone-700" },
      outline: { label: "Outlined", icon: Clock, color: "bg-blue-200 text-blue-700" },
      writing: { label: "Writing", icon: Clock, color: "bg-amber-200 text-amber-700" },
      partial: { label: "In Progress", icon: Clock, color: "bg-purple-200 text-purple-700" },
      generating_batch: { label: "Generating", icon: Sparkles, color: "bg-blue-300 text-blue-800 animate-pulse" },
      completed: { label: "Completed", icon: CheckCircle, color: "bg-green-200 text-green-700" },
    };

    const badge = badges[status] || badges.draft;
    const Icon = badge.icon;

    return (
      <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="h-3 w-3" />
        {badge.label}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-stone-50 to-neutral-100 flex">
      {/* Sidebar */}
      <Sidebar onLogout={onLogout} />

      {/* Main Content with left margin for sidebar */}
      <div className="flex-1 ml-0 md:ml-64">
        {/* Promo Banner */}
        {activePromo && activePromo.promo_type === "bogo" && (
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3 px-4">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 text-center">
              <div className="flex items-center gap-2">
                <Gift className="h-5 w-5" />
                <span className="font-bold text-lg">BUY ONE GET ONE FREE!</span>
              </div>
              <span className="text-green-100">Purchase credits now and get DOUBLE!</span>
              {activePromo.end_date ? (
                <div className="bg-white/20 px-3 py-1 rounded-full text-sm font-semibold">
                  {(() => {
                    const endDate = new Date(activePromo.end_date);
                    const now = new Date();
                    const daysLeft = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
                    if (daysLeft <= 0) return "Last Day!";
                    if (daysLeft === 1) return "Ends Tomorrow!";
                    if (daysLeft <= 7) return `${daysLeft} days left!`;
                    return `Ends ${endDate.toLocaleDateString()}`;
                  })()}
                </div>
              ) : (
                <div className="bg-white/20 px-3 py-1 rounded-full text-sm font-semibold">
                  Limited Time Offer
                </div>
              )}
              <Button 
                size="sm" 
                variant="secondary"
                className="bg-white text-green-700 hover:bg-green-50"
                onClick={() => navigate("/get-credits")}
              >
                Get Credits
              </Button>
            </div>
          </div>
        )}
        
        {activePromo && activePromo.promo_type === "percentage" && (
          <div className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white py-3 px-4">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 text-center">
              <div className="flex items-center gap-2">
                <Percent className="h-5 w-5" />
                <span className="font-bold text-lg">{activePromo.discount_percent}% OFF - {activePromo.name}!</span>
              </div>
              <span className="text-purple-100">Limited time discount on all credit packages</span>
              <Button 
                size="sm" 
                variant="secondary"
                className="bg-white text-purple-700 hover:bg-purple-50"
                onClick={() => navigate("/get-credits")}
              >
                Get Credits
              </Button>
            </div>
          </div>
        )}
        
        {/* Header */}
        <div className="border-b border-stone-200 bg-white/50 backdrop-blur-sm sticky top-0 z-10">
          <div className="px-8 py-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <span className="text-8xl font-bold playfair text-stone-900">DTR</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-900 rounded-full border border-amber-200">
                  <CreditCard className="h-4 w-4" />
                  <span className="text-sm font-semibold">{userCredits} {userCredits === 1 ? 'Credit' : 'Credits'}</span>
                </div>
                <span className="text-sm text-stone-600">Welcome, <span className="font-semibold">{user.name}</span></span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold playfair text-stone-900 mb-2">My Books</h1>
            <p className="text-stone-600">Manage your AI-generated book projects</p>
          </div>
          <Button 
            data-testid="new-book-btn"
            onClick={() => {
              console.log("NEW BOOK CLICKED - Checking for auto-saved data");
              
              // Check for auto-saved data
              const autoSavedData = localStorage.getItem('bookCreationAutoSave');
              if (autoSavedData) {
                try {
                  const parsed = JSON.parse(autoSavedData);
                  const savedTime = new Date(parsed.timestamp);
                  const hoursSinceSave = (new Date() - savedTime) / (1000 * 60 * 60);
                  
                  // Only restore if saved within last 24 hours
                  if (hoursSinceSave < 24 && (parsed.formData.premise || parsed.formData.title)) {
                    // Ask user if they want to restore
                    if (window.confirm(`Found unsaved work from ${savedTime.toLocaleString()}. Would you like to restore it?`)) {
                      // Restore the data
                      setFormData(parsed.formData || formData);
                      setProjectTitle(parsed.projectTitle || '');
                      setSelectedThemes(parsed.selectedThemes || []);
                      setSelectedTropes(parsed.selectedTropes || []);
                      setCharacters(parsed.characters || []);
                      setWritingStyle(parsed.writingStyle || writingStyle);
                      setSelectedIdea(parsed.selectedIdea);
                      setBookIdeas(parsed.bookIdeas || []);
                      
                      toast.success("💾 Your work has been restored!");
                      
                      // Open the book creation form directly
                      setCurrentDraftId(null);
                      setShowNewBook(true);
                      return;
                    } else {
                      // User declined, clear auto-save
                      localStorage.removeItem('bookCreationAutoSave');
                    }
                  }
                } catch (error) {
                  console.error('[Auto-Save] Failed to restore:', error);
                }
              }
              
              setCurrentDraftId(null); // Clear draft ID for fresh book
              setShowGenreChoice(true);
            }}
            size="lg"
            className="bg-amber-900 hover:bg-amber-800 text-white rounded-full shadow-lg"
          >
            <Plus className="h-5 w-5 mr-2" />
            New Book
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="spinner"></div>
          </div>
        ) : books.length === 0 ? (
          <Card className="max-w-md mx-auto mt-12 border-stone-200 shadow-lg" data-testid="empty-state">
            <CardContent className="pt-12 pb-12 text-center">
              <BookOpen className="h-16 w-16 text-stone-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold playfair text-stone-900 mb-2">No books yet</h3>
              <p className="text-stone-600 mb-6">Start your writing journey by creating your masterpiece</p>
              <Button 
                data-testid="empty-new-book-btn"
                onClick={() => setShowGenreChoice(true)}
                className="bg-amber-900 hover:bg-amber-800 text-white rounded-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Your Masterpiece
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="books-grid">
            {books.map((book) => (
              <Card 
                key={book.id} 
                className="book-card cursor-pointer hover:border-amber-900 transition-all border-stone-200 shadow-md overflow-hidden"
                onClick={() => {
                  if (book.status === 'draft') {
                    // Resume editing draft
                    resumeDraft(book);
                  } else {
                    // View completed/generating book
                    navigate(`/book/${book.id}`);
                  }
                }}
                data-testid={`book-card-${book.id}`}
              >
                <div className="h-2 bg-gradient-to-r from-amber-900 to-stone-700"></div>
                <CardHeader>
                  <div className="flex justify-between items-start mb-2">
                    <CardTitle className="text-xl playfair text-stone-900 line-clamp-2">{book.title}</CardTitle>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem 
                          onClick={(e) => {
                            e.stopPropagation();
                            setBookToEdit(book);
                            setEditingTitle(book.title);
                          }}
                        >
                          <Pencil className="mr-2 h-4 w-4" />
                          Edit Title
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          className="text-red-600 focus:text-red-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            setBookToDelete(book);
                          }}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  {getStatusBadge(book.status)}
                  <CardDescription className="mt-3 line-clamp-3">{book.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-between items-center text-sm text-stone-600 mb-3">
                    <span className="capitalize">{book.genre}</span>
                    {book.total_chapters && (
                      <span>{book.chapters_completed || 0}/{book.total_chapters} chapters</span>
                    )}
                  </div>
                  
                  {/* Resume Generation Button for Partial/Stalled/Generating Books */}
                  {(book.status === 'partial' || book.status === 'error' || book.status === 'generating_batch' || book.status === 'writing') && 
                   book.chapters_completed > 0 && 
                   book.chapters_completed < book.total_chapters && (
                    <Button
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const response = await axios.post(`${API}/books/${book.id}/resume-generation`);
                          toast.success(response.data.message);
                          // Reload books to show updated status
                          await loadBooks();
                        } catch (error) {
                          toast.error(error.response?.data?.detail || "Failed to resume generation");
                        }
                      }}
                      className="w-full bg-amber-900 hover:bg-amber-800 text-white"
                      size="sm"
                    >
                      <Clock className="h-4 w-4 mr-2" />
                      Resume Generation
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Genre Choice Dialog */}
      <Dialog open={showGenreChoice} onOpenChange={setShowGenreChoice}>
        <DialogContent className="sm:max-w-md" data-testid="genre-choice-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl playfair text-stone-900 text-center">
              Choose Your Genre
            </DialogTitle>
          </DialogHeader>
          
          {/* Saved Progress Indicator */}
          {localStorage.getItem('bookCreationProgress') && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-amber-600" />
                <span className="text-sm text-amber-900">Progress from previous session restored</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  clearSavedProgress();
                  resetForm();
                  toast.success('Progress cleared. Starting fresh!');
                }}
                className="text-xs text-amber-700 hover:text-amber-900"
              >
                Clear & Start Fresh
              </Button>
            </div>
          )}
          
          <div className="space-y-4 py-6">
            <p className="text-center text-stone-600">
              What type of book are you creating?
            </p>

            <div className="grid grid-cols-2 gap-4">
              <Button
                data-testid="choose-fiction"
                onClick={() => {
                  setFormData({ ...formData, genre: "fiction" });
                  setShowGenreChoice(false);
                  setShowContentChoice(true);
                }}
                className="h-32 flex flex-col items-center justify-center gap-3 bg-amber-900 hover:bg-amber-800 text-white"
              >
                <BookOpen className="h-12 w-12" />
                <span className="text-lg font-semibold">Fiction</span>
              </Button>

              <Button
                data-testid="choose-nonfiction"
                onClick={() => {
                  setFormData({ ...formData, genre: "non-fiction" });
                  setShowGenreChoice(false);
                  setShowContentChoice(true);
                }}
                className="h-32 flex flex-col items-center justify-center gap-3 bg-stone-700 hover:bg-stone-800 text-white"
              >
                <BookMarked className="h-12 w-12" />
                <span className="text-lg font-semibold">Non-Fiction</span>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Content Choice Dialog */}
      <Dialog open={showContentChoice} onOpenChange={setShowContentChoice}>
        <DialogContent className="sm:max-w-md" data-testid="content-choice-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl playfair text-stone-900 text-center">
              How Would You Like to Start?
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-6">
            <p className="text-center text-stone-600">
              Choose your starting point
            </p>

            <div className="grid grid-cols-2 gap-4">
              <Button
                data-testid="choose-fresh-start"
                onClick={() => {
                  setContentMode("fresh");
                  setShowContentChoice(false);
                  setShowNewBook(true);
                }}
                className="h-40 flex flex-col items-center justify-center gap-3 bg-green-700 hover:bg-green-800"
              >
                <FileText className="h-12 w-12" />
                <span className="text-lg font-semibold text-center">Start From Beginning</span>
                <span className="text-xs text-white/80">Create a brand new book</span>
              </Button>

              <Button
                data-testid="choose-import"
                onClick={() => {
                  setContentMode("import");
                  setShowContentChoice(false);
                  setShowNewBook(true);
                }}
                className="h-40 flex flex-col items-center justify-center gap-3 bg-blue-700 hover:bg-blue-800"
              >
                <Upload className="h-12 w-12" />
                <span className="text-lg font-semibold text-center">Import Existing Content</span>
                <span className="text-xs text-white/80">Upload notes or manuscript</span>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Project Name Dialog */}
      <Dialog open={showProjectName} onOpenChange={setShowProjectName}>
        <DialogContent className="sm:max-w-md" data-testid="project-title-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl playfair text-stone-900">
              Create Your Masterpiece
              {console.log("PROJECT NAME DIALOG RENDERED - showProjectName:", showProjectName) || ""}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 pt-4">
            <div className="text-center mb-6">
              <BookOpen className="h-16 w-16 text-amber-900 mx-auto mb-4" />
              <p className="text-stone-600">Give your project a name to get started</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="project-title" className="text-base font-semibold">Project Title</Label>
              <Input
                id="project-title"
                data-testid="project-title-input"
                placeholder="e.g., My First Novel, Mystery Series, etc."
                value={projectTitle}
                onChange={(e) => setProjectTitle(e.target.value)}
                className="text-lg"
                autoFocus
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowProjectName(false);
                  setProjectTitle("");
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (!projectTitle.trim()) {
                    toast.error("Please enter a project title");
                    return;
                  }
                  setFormData({ ...formData, title: projectTitle });
                  setShowProjectName(false);
                  setShowNewBook(true);
                }}
                disabled={!projectTitle.trim()}
                className="flex-1 bg-amber-900 hover:bg-amber-800 text-white"
                data-testid="continue-to-book-btn"
              >
                Continue
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Book Dialog */}
      <Dialog open={showNewBook} onOpenChange={setShowNewBook}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-book-dialog">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="text-2xl playfair text-stone-900">
                {contentMode === "import" ? "Import Your Content" : "Create Your Masterpiece"}
              </DialogTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={saveDraftAndReturn}
                disabled={creating}
                className="text-xs"
              >
                💾 Save Draft
              </Button>
            </div>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Show ONLY file upload if Import mode */}
            {contentMode === "import" ? (
              <div className="space-y-4">
                <p className="text-center text-stone-600">
                  Upload your notes, outline, or partial manuscript. Our AI will analyze it and help you create a complete book.
                </p>

                <div className="space-y-2">
                  <Label htmlFor="file-upload">Upload Your File *</Label>
                  
                  {!uploadedFile ? (
                    <>
                      <Input
                        id="file-upload"
                        type="file"
                        accept=".txt,.doc,.docx,.pdf,text/plain,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            // Validate file size (max 10MB)
                            if (file.size > 10 * 1024 * 1024) {
                              toast.error("File is too large. Maximum size is 10MB.");
                              e.target.value = ''; // Reset input
                              return;
                            }
                            // Validate file type
                            const validTypes = ['text/plain', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/pdf'];
                            const validExtensions = ['.txt', '.doc', '.docx', '.pdf'];
                            const fileName = file.name.toLowerCase();
                            const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));
                            const hasValidType = validTypes.includes(file.type) || file.type === '' || file.type === 'application/octet-stream';
                            
                            if (!hasValidExtension && !hasValidType) {
                              toast.error("Please upload a TXT, DOC, DOCX, or PDF file.");
                              e.target.value = ''; // Reset input
                              return;
                            }
                            setUploadedFile(file);
                            toast.success(`File uploaded: ${file.name}`);
                          }
                        }}
                      />
                      <label 
                        htmlFor="file-upload" 
                        className="cursor-pointer block border-2 border-dashed border-amber-400 bg-amber-50 rounded-lg p-8 text-center hover:border-amber-600 hover:bg-amber-100 transition-all"
                      >
                        <Upload className="h-12 w-12 text-amber-700 mx-auto mb-3" />
                        <p className="text-lg text-stone-900 mb-2 font-bold">
                          Click Here to Upload Your File
                        </p>
                        <p className="text-base text-stone-700 mb-1">
                          or drag and drop
                        </p>
                        <p className="text-sm text-stone-500">
                          TXT, DOC, DOCX, or PDF (Max 10MB)
                        </p>
                      </label>
                    </>
                  ) : (
                    <div className="border-2 border-green-500 bg-green-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <FileText className="h-10 w-10 text-green-700" />
                          <div>
                            <p className="font-semibold text-green-900 text-lg">{uploadedFile.name}</p>
                            <p className="text-sm text-green-700">
                              {(uploadedFile.size / 1024).toFixed(1)} KB • Ready to process
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setUploadedFile(null);
                            toast.info("File removed");
                          }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-5 w-5" />
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  <p className="text-xs text-stone-500 italic text-center">
                    AI will extract characters, plot points, themes, and writing style from your content
                  </p>
                </div>
              </div>
            ) : (
              // Show full form if Fresh Start mode
              <>
                <div className="space-y-2">
                  <Label htmlFor="project-title-main">Project Title *</Label>
                  <Input
                    id="project-title-main"
                    data-testid="project-title-main-input"
                    placeholder="e.g., My Fantasy Series, The Mystery Novel, etc."
                    value={projectTitle}
                    onChange={(e) => setProjectTitle(e.target.value)}
                    className="text-lg font-semibold"
                  />
                  <p className="text-xs text-stone-500">Give your project a memorable name</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="title">Book Title *</Label>
                  <Input
                    id="title"
                    data-testid="book-title-input"
                    placeholder="Enter your book title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="premise" className="text-base font-semibold flex items-center gap-2">
                    <span className="text-amber-600">★</span> Book Premise *
                  </Label>
                  <Textarea
                    id="premise"
                    data-testid="book-premise-input"
                    placeholder="What is your book REALLY about? Describe the core concept, main conflict, or central story idea that drives everything..."
                    value={formData.premise}
                    onChange={(e) => setFormData({ ...formData, premise: e.target.value })}
                    rows={5}
                    className="text-base"
                  />
                  <p className="text-xs text-amber-700 font-medium">
                    ⚡ This is THE MOST IMPORTANT field - all book ideas and content will be based heavily on this premise!
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Additional Details (Optional)</Label>
                  <Textarea
                    id="description"
                    data-testid="book-description-input"
                    placeholder="Add any extra details, character notes, setting information, or themes..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                  />
                </div>
              </>
            )}

            {/* Show Genre and Chapters only for Fresh Start */}
            {contentMode === "fresh" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="genre">Genre</Label>
              <Select 
                value={formData.genre} 
                onValueChange={(value) => setFormData({ ...formData, genre: value })}
              >
                <SelectTrigger data-testid="genre-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fiction">Fiction</SelectItem>
                  <SelectItem value="non-fiction">Non-Fiction</SelectItem>
                  <SelectItem value="mystery">Mystery</SelectItem>
                  <SelectItem value="thriller">Thriller</SelectItem>
                  <SelectItem value="romance">Romance</SelectItem>
                  <SelectItem value="sci-fi">Science Fiction</SelectItem>
                  <SelectItem value="fantasy">Fantasy</SelectItem>
                  <SelectItem value="horror">Horror</SelectItem>
                  <SelectItem value="biography">Biography</SelectItem>
                  <SelectItem value="self-help">Self-Help</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Case Studies Option - Only for Non-Fiction */}
            {(formData.genre === "non-fiction" || formData.genre === "self-help" || formData.genre === "biography") && (
              <div className="flex items-start space-x-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <Checkbox
                  id="includeCaseStudies"
                  data-testid="include-case-studies-checkbox"
                  checked={formData.includeCaseStudies}
                  onCheckedChange={(checked) => setFormData({ ...formData, includeCaseStudies: checked })}
                  className="mt-0.5"
                />
                <div className="space-y-1">
                  <Label 
                    htmlFor="includeCaseStudies" 
                    className="text-sm font-medium text-stone-900 cursor-pointer"
                  >
                    Include Case Studies
                  </Label>
                  <p className="text-xs text-stone-600">
                    Add practical real-world examples and case studies to each chapter, helping readers understand concepts in a practical context.
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-4">
              {/* Number of Chapters */}
              <div className="space-y-2">
                <Label htmlFor="chapters-fresh">Number of Chapters</Label>
                <div className="text-center mb-4">
                  <span className="text-3xl font-bold text-amber-900">{formData.total_chapters || 15}</span>
                  <span className="text-sm text-stone-600 ml-2">chapters</span>
                </div>
                <Slider
                  value={[parseInt(formData.total_chapters) || 15]}
                  onValueChange={(value) => {
                    const chapters = value[0];
                    const calculatedWordCount = chapters * 3300;
                    setFormData({ 
                      ...formData, 
                      total_chapters: chapters.toString(),
                      targetWordCount: calculatedWordCount
                    });
                  }}
                  min={10}
                  max={40}
                  step={1}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-stone-500">
                  <span>10 chapters</span>
                  <span>40 chapters</span>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
                  <p className="text-sm text-stone-700">
                    <span className="font-semibold">Words per Chapter: </span>
                    ~3,300 words
                  </p>
                  <p className="text-sm text-stone-700 mt-1">
                    <span className="font-semibold">Estimated Total Word Count: </span>
                    ~{((parseInt(formData.total_chapters) || 15) * 3300).toLocaleString()} words
                  </p>
                </div>
              </div>

              {/* Tropes & Themes Section */}
              <TropesSelector
                selectedThemes={selectedThemes}
                selectedTropes={selectedTropes}
                onThemesChange={setSelectedThemes}
                onTropesChange={setSelectedTropes}
                genre={formData.genre}
              />
            </div>
              </>
            )}

            <div className="flex gap-3 pt-4">
              <Button 
                data-testid="cancel-book-btn"
                variant="outline" 
                onClick={() => {
                  setShowNewBook(false);
                  setProjectTitle("");
                  setUploadedFile(null);
                  setContentMode("fresh");
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                data-testid="create-book-btn"
                onClick={async () => {
                  if (contentMode === "import" && uploadedFile) {
                    // Read and analyze the file
                    setCreating(true);
                    try {
                      // Create FormData to send file - Safari compatible
                      const formDataToSend = new FormData();
                      
                      // For Safari/iOS compatibility, create a new Blob with explicit type
                      const fileBlob = new Blob([uploadedFile], { type: uploadedFile.type || 'application/octet-stream' });
                      formDataToSend.append('file', fileBlob, uploadedFile.name);
                      formDataToSend.append('genre', formData.genre);
                      
                      // Call backend to analyze content
                      // Note: Don't set Content-Type header - let browser set it with boundary
                      const response = await axios.post(`${API}/analyze-imported-content`, formDataToSend, {
                        timeout: 120000, // 2 minute timeout for large files
                      });
                      
                      // Store analyzed content including chapter outline for non-fiction
                      const analyzedData = {
                        characters: response.data.characters,
                        summary: response.data.summary
                      };
                      
                      // If this is a chapter-based outline (non-fiction), store the chapters
                      if (response.data.has_chapter_outline && response.data.chapters) {
                        analyzedData.chapters = response.data.chapters;
                        analyzedData.chapter_count = response.data.chapter_count;
                        console.log(`[Import] Extracted ${response.data.chapter_count} chapters from outline`);
                        
                        // DON'T override user's chapter selection - they can adjust it
                        // Only set total_chapters if user hasn't already selected a value
                        const currentChapters = parseInt(formData.total_chapters) || 0;
                        const importedChapters = response.data.chapter_count;
                        
                        // Use the HIGHER of: user's selection OR imported chapter count
                        const finalChapters = Math.max(currentChapters, importedChapters, 15);
                        
                        setFormData({
                          ...formData, 
                          total_chapters: finalChapters.toString(),
                          description: response.data.summary
                        });
                        
                        if (importedChapters < finalChapters) {
                          toast.success(`Extracted ${importedChapters} chapters. Keeping your selection of ${finalChapters} chapters - additional chapters will be generated.`);
                        } else {
                          toast.success(`Extracted ${importedChapters} chapter outline! Your chapter structure will be preserved.`);
                        }
                      } else {
                        // Fiction - pre-populate characters
                        const extractedChars = response.data.characters.map(name => ({
                          name: name,
                          role: "",
                          description: ""
                        }));
                        setCharacters(extractedChars);
                        
                        // Update description with summary
                        if (response.data.summary) {
                          setFormData({...formData, description: response.data.summary});
                        }
                        
                        toast.success(`Extracted ${extractedChars.length} characters from your content!`);
                      }
                      
                      setAnalyzedContent(analyzedData);
                      
                      // Move to configuration screen
                      setShowNewBook(false);
                      setShowImportConfig(true);
                    } catch (error) {
                      console.error("Analyze content error:", error);
                      const errorMessage = error.response?.data?.detail || "Failed to analyze content. Please try again.";
                      toast.error(errorMessage);
                    } finally {
                      setCreating(false);
                    }
                  } else {
                    // Start from Beginning - go to Character Development
                    if (!projectTitle || !formData.title || !formData.description) {
                      toast.error("Please fill in all required fields");
                      return;
                    }
                    setShowNewBook(false);
                    setShowCharacterDev(true);
                  }
                }}
                disabled={creating || (contentMode === "import" && !uploadedFile)}
                className="flex-1 bg-amber-900 hover:bg-amber-800 text-white"
              >
                {creating ? "Creating..." : (contentMode === "import" ? "Analyze & Continue" : "Create Book")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Configuration Dialog */}
      <Dialog open={showImportConfig} onOpenChange={setShowImportConfig}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="text-2xl playfair text-stone-900">Configure Your Book</DialogTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={saveDraftAndReturn}
                disabled={creating}
                className="text-xs"
              >
                💾 Save Draft
              </Button>
            </div>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-green-700" />
                <span className="text-sm font-semibold text-green-900">File: {uploadedFile?.name}</span>
              </div>
            </div>

            <div className="space-y-4">
              {/* Premise Field */}
              <div className="space-y-2">
                <Label htmlFor="premise-import" className="text-base font-semibold flex items-center gap-2">
                  <span className="text-amber-600">★</span> Book Premise *
                </Label>
                <Textarea
                  id="premise-import"
                  placeholder="What is your book REALLY about? Describe the core concept, main conflict, or central story idea..."
                  value={formData.premise}
                  onChange={(e) => setFormData({ ...formData, premise: e.target.value })}
                  rows={5}
                  className="text-base"
                />
                <p className="text-xs text-amber-700 font-medium">
                  ⚡ This is THE MOST IMPORTANT field - all book ideas will be based heavily on this premise!
                </p>
              </div>

              <div className="space-y-2">
                <Label>Tone</Label>
                <Select 
                  value={importConfig.tone} 
                  onValueChange={(value) => setImportConfig({...importConfig, tone: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="engaging">Engaging</SelectItem>
                    <SelectItem value="serious">Serious</SelectItem>
                    <SelectItem value="humorous">Humorous</SelectItem>
                    <SelectItem value="inspirational">Inspirational</SelectItem>
                    <SelectItem value="dramatic">Dramatic</SelectItem>
                    <SelectItem value="mysterious">Mysterious</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-4">
                {/* Number of Chapters */}
                <div className="space-y-2">
                  <Label htmlFor="chapters">Number of Chapters</Label>
                  <div className="text-center mb-4">
                    <span className="text-3xl font-bold text-amber-900">{importConfig.chapters}</span>
                    <span className="text-sm text-stone-600 ml-2">chapters</span>
                  </div>
                  <Slider
                    value={[importConfig.chapters]}
                    onValueChange={(value) => {
                      const chapters = value[0];
                      const calculatedWordCount = chapters * 3300;
                      setImportConfig({
                        ...importConfig, 
                        chapters: chapters,
                        targetWordCount: calculatedWordCount
                      });
                    }}
                    min={10}
                    max={40}
                    step={1}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-stone-500">
                    <span>10 chapters</span>
                    <span>40 chapters</span>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
                    <p className="text-sm text-stone-700">
                      <span className="font-semibold">Words per Chapter: </span>
                      ~3,300 words
                    </p>
                    <p className="text-sm text-stone-700 mt-1">
                      <span className="font-semibold">Estimated Total Word Count: </span>
                      ~{(importConfig.chapters * 3300).toLocaleString()} words
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="genre-config">Genre</Label>
                <Select 
                  value={formData.genre} 
                  onValueChange={(value) => setFormData({...formData, genre: value})}
                >
                  <SelectTrigger id="genre-config">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fiction">Fiction</SelectItem>
                    <SelectItem value="non-fiction">Non-Fiction</SelectItem>
                    <SelectItem value="mystery">Mystery</SelectItem>
                    <SelectItem value="romance">Romance</SelectItem>
                    <SelectItem value="sci-fi">Science Fiction</SelectItem>
                    <SelectItem value="fantasy">Fantasy</SelectItem>
                    <SelectItem value="thriller">Thriller</SelectItem>
                    <SelectItem value="horror">Horror</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <TropesSelector
                selectedThemes={selectedThemes}
                selectedTropes={selectedTropes}
                onThemesChange={setSelectedThemes}
                onTropesChange={setSelectedTropes}
                genre={formData.genre}
              />
            </div>

            <div className="flex gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setShowImportConfig(false);
                  setShowNewBook(true);
                }}
                className="flex-1"
              >
                Back
              </Button>
              <Button
                onClick={() => {
                  setShowImportConfig(false);
                  setShowCharacterDev(true);
                }}
                className="flex-1 bg-amber-900 hover:bg-amber-800 text-white"
              >
                Character Development
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Character Development Dialog */}
      <Dialog open={showCharacterDev} onOpenChange={setShowCharacterDev}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="text-2xl playfair text-stone-900">Character Development</DialogTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={saveDraftAndReturn}
                disabled={creating}
                className="text-xs"
              >
                💾 Save Draft
              </Button>
            </div>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-green-700" />
                <span className="text-sm font-semibold text-green-900">File: {uploadedFile?.name}</span>
              </div>
            </div>

            <p className="text-stone-600">
              Let's develop the main characters for your story. You can add as many characters as you need.
            </p>

            {/* Character List */}
            <div className="space-y-4">
              {characters.map((char, index) => (
                <Card key={index} className="border-stone-200">
                  <CardContent className="pt-6">
                    <div className="flex justify-between items-start mb-4">
                      <h4 className="font-semibold text-stone-900">Character {index + 1}</h4>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newChars = characters.filter((_, i) => i !== index);
                          setCharacters(newChars);
                        }}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <Label>Name</Label>
                        <Input
                          value={char.name}
                          onChange={(e) => {
                            const newChars = [...characters];
                            newChars[index].name = e.target.value;
                            setCharacters(newChars);
                          }}
                          placeholder="Character name"
                        />
                      </div>
                      <div>
                        <Label>Role</Label>
                        <Select
                          value={char.role}
                          onValueChange={(value) => {
                            const newChars = [...characters];
                            newChars[index].role = value;
                            setCharacters(newChars);
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="protagonist">Protagonist</SelectItem>
                            <SelectItem value="antagonist">Antagonist</SelectItem>
                            <SelectItem value="supporting">Supporting Character</SelectItem>
                            <SelectItem value="mentor">Mentor</SelectItem>
                            <SelectItem value="love-interest">Love Interest</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Textarea
                          value={char.description}
                          onChange={(e) => {
                            const newChars = [...characters];
                            newChars[index].description = e.target.value;
                            setCharacters(newChars);
                          }}
                          placeholder="Physical appearance, personality traits, background..."
                          rows={3}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* Add Character Button */}
              <Button
                variant="outline"
                onClick={() => {
                  setCharacters([
                    ...characters,
                    { name: "", role: "protagonist", description: "" }
                  ]);
                }}
                className="w-full border-dashed border-2 border-stone-300 hover:border-amber-600 hover:bg-amber-50"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Character
              </Button>
            </div>

            <div className="flex gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCharacterDev(false);
                  setShowImportConfig(true);
                }}
                className="flex-1"
              >
                Back
              </Button>
              <Button
                onClick={handleGenerateIdeas}
                disabled={generatingIdeas}
                className="flex-1 bg-amber-900 hover:bg-amber-800 text-white"
              >
                {generatingIdeas ? "Generating..." : "Generate Ideas"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Book Ideas Dialog */}
      <Dialog open={showBookIdeas} onOpenChange={setShowBookIdeas}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="text-2xl playfair text-stone-900">Book Ideas</DialogTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={saveDraftAndReturn}
                disabled={creating}
                className="text-xs"
              >
                💾 Save Draft
              </Button>
            </div>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-green-700" />
                <span className="text-sm font-semibold text-green-900">File: {uploadedFile?.name}</span>
              </div>
            </div>

            {generatingIdeas ? (
              <div className="flex flex-col items-center justify-center py-12 space-y-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-900"></div>
                <p className="text-stone-600 text-center">
                  Generating creative book ideas based on your characters and preferences...
                </p>
              </div>
            ) : (
              <>
                <p className="text-stone-600">
                  Select an idea that resonates with you, or refine these suggestions below.
                </p>

                {/* Ideas Grid */}
                <div className="grid md:grid-cols-2 gap-4">
                  {bookIdeas.map((idea, index) => (
                    <Card 
                      key={index} 
                      className={`cursor-pointer transition-all hover:shadow-lg ${
                        selectedIdea === index 
                          ? 'border-2 border-amber-600 bg-amber-50' 
                          : 'border-stone-200 hover:border-amber-400'
                      }`}
                      onClick={() => setSelectedIdea(index)}
                    >
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between mb-3">
                          <h4 className="font-semibold text-stone-900 text-lg">Idea {index + 1}</h4>
                          {selectedIdea === index && (
                            <CheckCircle className="h-5 w-5 text-amber-600" />
                          )}
                        </div>
                        <p className="text-sm text-stone-700 leading-relaxed">{idea}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Refine Ideas Section */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-blue-700" />
                    <h4 className="font-semibold text-blue-900">Refine These Ideas</h4>
                  </div>
                  <p className="text-sm text-blue-800">
                    Not quite what you're looking for? Provide additional guidance and we'll generate new ideas.
                  </p>
                  <Textarea
                    placeholder="E.g., 'Make it darker and more mysterious' or 'Add more romance' or 'Focus on political intrigue'..."
                    value={refinePrompt}
                    onChange={(e) => setRefinePrompt(e.target.value)}
                    rows={3}
                    className="bg-white"
                  />
                  <Button
                    onClick={handleRefineIdeas}
                    disabled={generatingIdeas || !refinePrompt.trim()}
                    variant="outline"
                    className="border-blue-600 text-blue-900 hover:bg-blue-100"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    Refine Ideas
                  </Button>
                </div>
              </>
            )}

            <div className="flex gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setShowBookIdeas(false);
                  setShowCharacterDev(true);
                }}
                disabled={generatingIdeas}
                className="flex-1"
              >
                Back to Characters
              </Button>
              <Button
                onClick={() => {
                  if (selectedIdea === null) {
                    toast.error("Please select an idea to continue");
                    return;
                  }
                  setShowBookIdeas(false);
                  setShowWritingStyle(true);
                }}
                disabled={generatingIdeas || selectedIdea === null}
                className="flex-1 bg-amber-900 hover:bg-amber-800 text-white"
              >
                Continue to Writing Style
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Writing Style Dialog */}
      <Dialog open={showWritingStyle} onOpenChange={setShowWritingStyle}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="text-2xl playfair text-stone-900">Configure Writing Style</DialogTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={saveDraftAndReturn}
                disabled={creating}
                className="text-xs"
              >
                💾 Save Draft
              </Button>
            </div>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-green-700" />
                <span className="text-sm font-semibold text-green-900">File: {uploadedFile?.name}</span>
              </div>
            </div>

            <p className="text-stone-600">
              Define the writing style and narrative approach for your book. These settings will guide the AI in generating content that matches your vision.
            </p>

            <div className="space-y-4">
              {/* Tone/Mood */}
              <div className="space-y-2">
                <Label htmlFor="tone-style">Tone & Mood</Label>
                <Select 
                  value={writingStyle.tone} 
                  onValueChange={(value) => setWritingStyle({...writingStyle, tone: value})}
                >
                  <SelectTrigger id="tone-style">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="engaging">Engaging & Captivating</SelectItem>
                    <SelectItem value="serious">Serious & Formal</SelectItem>
                    <SelectItem value="humorous">Humorous & Light</SelectItem>
                    <SelectItem value="inspirational">Inspirational & Uplifting</SelectItem>
                    <SelectItem value="dramatic">Dramatic & Intense</SelectItem>
                    <SelectItem value="mysterious">Mysterious & Suspenseful</SelectItem>
                    <SelectItem value="romantic">Romantic & Emotional</SelectItem>
                    <SelectItem value="dark">Dark & Gritty</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-stone-500">The overall emotional tone of your narrative</p>
              </div>

              {/* Narrative Perspective */}
              <div className="space-y-2">
                <Label htmlFor="perspective">Narrative Perspective</Label>
                <Select 
                  value={writingStyle.perspective} 
                  onValueChange={(value) => setWritingStyle({...writingStyle, perspective: value})}
                >
                  <SelectTrigger id="perspective">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="first-person">First Person (I, We)</SelectItem>
                    <SelectItem value="second-person">Second Person (You)</SelectItem>
                    <SelectItem value="third-person">Third Person Limited (He, She, They)</SelectItem>
                    <SelectItem value="third-person-omniscient">Third Person Omniscient</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-stone-500">The point of view from which the story is told</p>
              </div>

              {/* Author Style */}
              <div className="space-y-2">
                <Label htmlFor="author-style">Writing Style</Label>
                <Select 
                  value={writingStyle.authorStyle} 
                  onValueChange={(value) => setWritingStyle({...writingStyle, authorStyle: value})}
                >
                  <SelectTrigger id="author-style">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="descriptive">Descriptive & Detailed</SelectItem>
                    <SelectItem value="concise">Concise & Direct</SelectItem>
                    <SelectItem value="poetic">Poetic & Lyrical</SelectItem>
                    <SelectItem value="conversational">Conversational & Casual</SelectItem>
                    <SelectItem value="literary">Literary & Sophisticated</SelectItem>
                    <SelectItem value="action-packed">Action-Packed & Fast</SelectItem>
                    <SelectItem value="dialogue-heavy">Dialogue-Heavy</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-stone-500">The prose style and level of detail</p>
              </div>

              {/* Pacing */}
              <div className="space-y-2">
                <Label htmlFor="pacing">Story Pacing</Label>
                <Select 
                  value={writingStyle.pacing} 
                  onValueChange={(value) => setWritingStyle({...writingStyle, pacing: value})}
                >
                  <SelectTrigger id="pacing">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fast">Fast-Paced (Quick action, rapid developments)</SelectItem>
                    <SelectItem value="moderate">Moderate (Balanced progression)</SelectItem>
                    <SelectItem value="slow">Slow-Burn (Gradual build-up, character focus)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-stone-500">How quickly the story unfolds</p>
              </div>

              {/* Author Inspiration */}
              <div className="space-y-2">
                <Label htmlFor="author-inspiration">Author Inspiration (Optional)</Label>
                <Select 
                  value={writingStyle.authorInspiration} 
                  onValueChange={(value) => setWritingStyle({...writingStyle, authorInspiration: value})}
                >
                  <SelectTrigger id="author-inspiration">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None / Original Style</SelectItem>
                    {/* Fiction Authors */}
                    <SelectItem value="stephen-king">Stephen King (Horror/Suspense, Character-driven)</SelectItem>
                    <SelectItem value="jk-rowling">J.K. Rowling (Fantasy, World-building)</SelectItem>
                    <SelectItem value="dan-brown">Dan Brown (Thriller, Fast-paced, Cliffhangers)</SelectItem>
                    <SelectItem value="agatha-christie">Agatha Christie (Mystery, Plot twists)</SelectItem>
                    <SelectItem value="ernest-hemingway">Ernest Hemingway (Concise, Powerful prose)</SelectItem>
                    <SelectItem value="nora-roberts">Nora Roberts (Romance, Emotional depth)</SelectItem>
                    <SelectItem value="james-patterson">James Patterson (Thriller, Short chapters)</SelectItem>
                    <SelectItem value="margaret-atwood">Margaret Atwood (Literary, Dystopian)</SelectItem>
                    <SelectItem value="john-grisham">John Grisham (Legal thriller, Realistic)</SelectItem>
                    <SelectItem value="tolkien">J.R.R. Tolkien (Epic fantasy, Detailed world)</SelectItem>
                    <SelectItem value="george-martin">George R.R. Martin (Dark fantasy, Complex plots)</SelectItem>
                    {/* Non-Fiction Authors - General */}
                    <SelectItem value="malcolm-gladwell">Malcolm Gladwell (Non-fiction, Storytelling with Data)</SelectItem>
                    <SelectItem value="michelle-obama">Michelle Obama (Memoir, Inspirational)</SelectItem>
                    <SelectItem value="brene-brown">Brené Brown (Self-help, Vulnerability Research)</SelectItem>
                    <SelectItem value="james-clear">James Clear (Habits, Practical Systems)</SelectItem>
                    <SelectItem value="cal-newport">Cal Newport (Productivity, Deep Work)</SelectItem>
                    {/* Non-Fiction Authors - Finance & Business */}
                    <SelectItem value="dave-ramsey">Dave Ramsey (Finance, Step-by-step Systems)</SelectItem>
                    <SelectItem value="robert-kiyosaki">Robert Kiyosaki (Finance, Wealth Mindset)</SelectItem>
                    <SelectItem value="ramit-sethi">Ramit Sethi (Personal Finance, Automation)</SelectItem>
                    <SelectItem value="morgan-housel">Morgan Housel (Behavioral Finance, Clear Wisdom)</SelectItem>
                    <SelectItem value="simon-sinek">Simon Sinek (Leadership, Start with Why)</SelectItem>
                    <SelectItem value="ray-dalio">Ray Dalio (Principles, Systematic Thinking)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-stone-500">Model your writing after a bestselling author&apos;s style</p>
              </div>

              {/* Book Structure */}
              <div className="space-y-2">
                <Label htmlFor="book-structure">Book Structure</Label>
                <Select 
                  value={writingStyle.bookStructure} 
                  onValueChange={(value) => setWritingStyle({...writingStyle, bookStructure: value})}
                >
                  <SelectTrigger id="book-structure">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="three-act">Three-Act Structure (Setup, Confrontation, Resolution)</SelectItem>
                    <SelectItem value="five-act">Five-Act Structure (Freytag's Pyramid)</SelectItem>
                    <SelectItem value="seven-point">Seven-Point Story Structure</SelectItem>
                    <SelectItem value="heros-journey">Hero's Journey (Monomyth - 12 stages)</SelectItem>
                    <SelectItem value="save-the-cat">Save the Cat (15 beats)</SelectItem>
                    <SelectItem value="in-media-res">In Media Res (Start in the middle)</SelectItem>
                    <SelectItem value="nonlinear">Non-Linear / Fractured Timeline</SelectItem>
                    <SelectItem value="episodic">Episodic (Self-contained chapters)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-stone-500">The narrative framework for your story</p>
              </div>

              {/* Keep this in mind box */}
              <div className="space-y-2">
                <Label htmlFor="special-instructions" className="text-base font-semibold">Keep this in mind</Label>
                <Textarea
                  id="special-instructions"
                  placeholder="Enter your final instructions, specific details, or unique elements you want the AI to remember while writing your story..."
                  value={writingStyle.specialInstructions}
                  onChange={(e) => setWritingStyle({...writingStyle, specialInstructions: e.target.value})}
                  className="min-h-[120px] resize-y"
                />
                <p className="text-xs text-stone-600">
                  <span className="font-medium">Important:</span> This is your last chance to guide the AI. Be specific about tone, themes, plot points, character traits, or any critical elements that should shape your story.
                </p>
              </div>

              {/* Style Preview Info Box */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                <h4 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  Your Writing Style Summary
                </h4>
                <div className="text-sm text-amber-900 space-y-1">
                  {writingStyle.tone && <p>• <span className="font-medium">Tone:</span> {writingStyle.tone.charAt(0).toUpperCase() + writingStyle.tone.slice(1)}</p>}
                  {writingStyle.perspective && <p>• <span className="font-medium">Perspective:</span> {writingStyle.perspective.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</p>}
                  {writingStyle.authorStyle && <p>• <span className="font-medium">Style:</span> {writingStyle.authorStyle.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</p>}
                  {writingStyle.pacing && <p>• <span className="font-medium">Pacing:</span> {writingStyle.pacing.charAt(0).toUpperCase() + writingStyle.pacing.slice(1)}</p>}
                  {writingStyle.authorInspiration && writingStyle.authorInspiration !== "none" && (
                    <p>• <span className="font-medium">Inspired by:</span> {writingStyle.authorInspiration.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</p>
                  )}
                  {writingStyle.bookStructure && <p>• <span className="font-medium">Structure:</span> {writingStyle.bookStructure.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</p>}
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setShowWritingStyle(false);
                  setShowBookIdeas(true);
                }}
                className="flex-1"
              >
                Back to Ideas
              </Button>
              <Button
                onClick={handleGeneratePreview}
                disabled={generatingPreview}
                className="flex-1 bg-amber-900 hover:bg-amber-800 text-white"
              >
                {generatingPreview ? "Generating..." : "Generate Preview"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="text-2xl playfair text-stone-900">First Page Preview</DialogTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={saveDraftAndReturn}
                disabled={creating}
                className="text-xs"
              >
                💾 Save Draft
              </Button>
            </div>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-green-700" />
                <span className="text-sm font-semibold text-green-900">File: {uploadedFile?.name}</span>
              </div>
            </div>

            {generatingPreview ? (
              <div className="flex flex-col items-center justify-center py-12 space-y-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-900"></div>
                <p className="text-stone-600 text-center">
                  Generating your first page preview based on your selected style...
                </p>
              </div>
            ) : (
              <>
                <div className="bg-white border-2 border-stone-200 rounded-lg p-6 shadow-inner">
                  <div className="prose prose-stone max-w-none">
                    <div className="whitespace-pre-wrap text-base leading-relaxed text-stone-900 font-serif">
                      {previewText || "Your preview will appear here..."}
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-900">
                    <span className="font-semibold">💡 Tip:</span> This is a sample of how your book will be written. 
                    You can regenerate with different settings or proceed to create the full book.
                  </p>
                </div>
              </>
            )}

            <div className="flex gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setShowPreview(false);
                  setShowWritingStyle(true);
                }}
                disabled={generatingPreview}
                className="flex-1"
              >
                Back to Style
              </Button>
              <Button
                variant="outline"
                onClick={handleGeneratePreview}
                disabled={generatingPreview}
                className="flex-1 border-amber-600 text-amber-900 hover:bg-amber-50"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Regenerate Preview
              </Button>
              <Button
                onClick={() => setShowConfirmCreate(true)}
                disabled={creating || generatingPreview}
                className="flex-1 bg-green-700 hover:bg-green-800"
              >
                Create Book Project
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Book Confirmation Dialog */}
      <AlertDialog open={showConfirmCreate} onOpenChange={setShowConfirmCreate}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-amber-600" />
              Create Your Book
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4 pt-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-base text-stone-900 font-semibold mb-2">
                  You're about to create a complete book with AI!
                </p>
                <p className="text-sm text-stone-700">
                  This will generate all {importConfig.chapters || 15} chapters based on your selected idea, characters, and writing style preferences.
                </p>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-blue-900">Credit Balance:</span>
                  <span className="text-lg font-bold text-blue-900">{userCredits.toFixed(1)} credits</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-blue-900">Cost:</span>
                  <span className="text-lg font-bold text-red-600">-1.0 credit</span>
                </div>
                <div className="border-t border-blue-300 my-2"></div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-blue-900">After Creation:</span>
                  <span className="text-lg font-bold text-green-700">{(userCredits - 1).toFixed(1)} credits</span>
                </div>
              </div>

              {userCredits < 1 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3">
                  <div>
                    <p className="text-sm text-red-800 font-semibold">
                      ⚠️ Insufficient Credits
                    </p>
                    <p className="text-sm text-red-700 mt-1">
                      You need at least 1 credit to create a book.
                    </p>
                  </div>
                  <Button
                    onClick={() => saveDraftAndNavigateToCredits()}
                    className="w-full bg-amber-600 hover:bg-amber-700"
                    disabled={creating}
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    {creating ? "Saving..." : "Get Credits"}
                  </Button>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={creating}>Cancel</AlertDialogCancel>
            {userCredits >= 1 && (
              <AlertDialogAction
                onClick={handleCreateBook}
                disabled={creating}
                className="bg-green-700 hover:bg-green-800"
              >
                {creating ? "Creating Book..." : "Confirm & Create"}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Book Generation Progress Dialog */}
      <Dialog open={showProgress} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-2xl" hideClose>
          <DialogHeader>
            <DialogTitle className="text-2xl playfair text-stone-900 flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-amber-600 animate-pulse" />
              Generating Your Book
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-6">
            {/* Progress Bar */}
            <div className="space-y-2">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-semibold text-stone-700">
                  {generationProgress.status}
                </span>
                <span className="text-2xl font-bold text-amber-600">
                  {generationProgress.percentage}%
                </span>
              </div>
              
              <div className="w-full bg-stone-200 rounded-full h-4 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-amber-500 to-amber-600 h-4 rounded-full transition-all duration-500 ease-out flex items-center justify-end pr-2"
                  style={{ width: `${generationProgress.percentage}%` }}
                >
                  {generationProgress.percentage > 10 && (
                    <span className="text-xs font-bold text-white">
                      {generationProgress.percentage}%
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Chapter Progress */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <BookOpen className="h-8 w-8 text-blue-600" />
                  <div>
                    <p className="text-sm font-semibold text-blue-900">Chapter Progress</p>
                    <p className="text-2xl font-bold text-blue-700">
                      {generationProgress.currentChapter} / {generationProgress.totalChapters}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-blue-700 font-medium">
                    {generationProgress.totalChapters - generationProgress.currentChapter} remaining
                  </p>
                </div>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-900">
                <span className="font-semibold">⏱️ Please wait:</span> Your book is being generated with AI. 
                This process may take about an hour depending on the number of chapters. 
                You can close this window and return later - we'll save your progress!
              </p>
            </div>

            {/* Animated Loading Indicator */}
            <div className="flex justify-center">
              <div className="flex space-x-2">
                <div className="w-3 h-3 bg-amber-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-3 h-3 bg-amber-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-3 h-3 bg-amber-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!bookToDelete} onOpenChange={() => setBookToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Book</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{bookToDelete?.title}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteBook}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Title Dialog */}
      <Dialog open={!!bookToEdit} onOpenChange={(open) => {
        if (!open) {
          setBookToEdit(null);
          setEditingTitle("");
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-amber-600" />
              Edit Book Title
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-title">New Title</Label>
              <Input
                id="edit-title"
                value={editingTitle}
                onChange={(e) => setEditingTitle(e.target.value)}
                placeholder="Enter new title"
                maxLength={200}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && editingTitle.trim()) {
                    handleEditTitle();
                  }
                }}
              />
              <p className="text-xs text-stone-500">{editingTitle.length}/200 characters</p>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => {
              setBookToEdit(null);
              setEditingTitle("");
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleEditTitle}
              disabled={!editingTitle.trim() || savingTitle}
              className="bg-amber-900 hover:bg-amber-800 text-white"
            >
              {savingTitle ? "Saving..." : "Save Title"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Resume Progress Dialog */}
      <AlertDialog open={showResumeDialog} onOpenChange={setShowResumeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-amber-600" />
              Resume Your Book Creation?
            </AlertDialogTitle>
            <AlertDialogDescription>
              We found your previous book creation progress. Would you like to continue where you left off or start fresh?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                clearSavedProgress();
                setShowResumeDialog(false);
                toast.success('Starting fresh! Previous progress cleared.');
              }}
            >
              Start Fresh
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowResumeDialog(false);
                const restored = restoreProgressFromLocalStorage();
                if (restored) {
                  // Set flag to trigger form opening after state updates
                  setRestoringProgress(true);
                }
              }}
              className="bg-amber-900 hover:bg-amber-800 text-white"
            >
              Resume Progress
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </div>
  );
};

export default Dashboard;